// Iteration 47 Phase 4 — Ledger drill-down modal.
// Opens when user clicks any KPI card. Fetches /api/ledger/drilldown
// and shows the underlying invoice rows.
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { X, ExternalLink, Loader2 } from "lucide-react";

export default function LedgerDrilldown({ metric, title, definition, onClose }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [count, setCount] = useState(0);

  useEffect(() => {
    setLoading(true);
    api.get(`/ledger/drilldown?metric=${metric}&limit=500`)
      .then((r) => { setRows(r.data.rows || []); setCount(r.data.count || 0); })
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [metric]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70" onClick={onClose}>
      <div className="bg-[#0B1120] border border-slate-700 rounded-xl max-w-4xl w-full max-h-[85vh] flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-slate-800">
          <div>
            <h2 className="text-lg font-bold text-slate-100">{title}</h2>
            <p className="text-xs text-slate-400 mt-1 max-w-2xl">{definition}</p>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-100"><X className="w-5 h-5" /></button>
        </div>
        <div className="px-4 py-2 text-xs text-slate-400 border-b border-slate-800">
          <span className="text-emerald-400 font-semibold">{count}</span> row(s) — showing first 500
        </div>
        <div className="overflow-y-auto flex-1">
          {loading ? (
            <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-500" /></div>
          ) : rows.length === 0 ? (
            <div className="p-8 text-center text-slate-500 text-sm">No rows</div>
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-slate-900/50 sticky top-0">
                <tr className="text-slate-400 uppercase tracking-wider">
                  <th className="text-left px-3 py-2">Invoice #</th>
                  <th className="text-left px-3 py-2">Type</th>
                  <th className="text-left px-3 py-2">Status</th>
                  <th className="text-left px-3 py-2">Customer / Vehicle</th>
                  <th className="text-right px-3 py-2">Total</th>
                  <th className="text-left px-3 py-2">Date</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-t border-slate-800 hover:bg-slate-900/40" data-testid={`row-${r.id}`}>
                    <td className="px-3 py-2 font-mono text-blue-400">{r.invoice_number || r.id?.slice(0, 8)}</td>
                    <td className="px-3 py-2">
                      <span className="text-[10px] uppercase px-1.5 py-0.5 rounded bg-slate-800 text-slate-300 border border-slate-700">
                        {r.type || "—"}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <span className={`text-[10px] uppercase px-1.5 py-0.5 rounded ${
                        r.status === "paid" ? "bg-emerald-500/20 text-emerald-300" :
                        r.status === "sent" ? "bg-blue-500/20 text-blue-300" :
                        r.status === "draft" ? "bg-slate-700 text-slate-300" :
                        r.status === "cancelled" ? "bg-rose-500/15 text-rose-300" :
                        "bg-amber-500/20 text-amber-300"
                      }`}>{r.status}</span>
                    </td>
                    <td className="px-3 py-2">
                      <div className="text-slate-200 truncate max-w-[240px]">{r.customer_name || r.recipient_name || "—"}</div>
                      <div className="text-slate-500 text-[10px] truncate max-w-[240px]">
                        {r.vehicle_year} {r.vehicle_make} {r.vehicle_model}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-emerald-400">${(r.total || 0).toFixed(2)}</td>
                    <td className="px-3 py-2 text-slate-400">{(r.paid_at || r.created_at || "").slice(0, 10)}</td>
                    <td className="px-3 py-2">
                      <Link to={`/invoices/${r.id}`} className="text-blue-400 hover:underline flex items-center gap-1">
                        <ExternalLink className="w-3 h-3" /> Open
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
